<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { BuildingIcon } from 'lucide-vue-next'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import EmptyState from '@/components/page/EmptyState.vue'
import Button from '@/components/ui/Button.vue'
import Drawer from '@/components/ui/Drawer.vue'
import { useAppStore } from '@/stores/app'
import { useImportErrorsStore } from '@/stores/importErrors'

const SIDEBAR_STORAGE_KEY = 'mobidic:sidebar-collapsed'

const readCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

const { t } = useI18n()
const route = useRoute()
const appStore = useAppStore()
const importErrors = useImportErrorsStore()

const collapsed = ref(readCollapsed())
watch(collapsed, (value) => {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(value))
  } catch {
    // Storage non disponibile: la scelta vale solo per la sessione.
  }
})

// Le view a pagina intera (es. /agent) gestiscono lo scroll al proprio interno.
const fullHeight = computed(() => route.meta?.fullHeight === true)

const drawerOpen = ref(false)
watch(
  () => route.fullPath,
  () => {
    drawerOpen.value = false
  }
)

onMounted(() => {
  importErrors.load()
  importErrors.startPolling()
})
onUnmounted(() => importErrors.stopPolling())
watch(
  () => appStore.currentCompany?.id,
  () => importErrors.load()
)
</script>

<template>
  <div class="flex h-screen overflow-hidden bg-surface-50 dark:bg-surface-950">
    <aside
      data-test="sidebar-desktop"
      class="hidden shrink-0 border-r border-surface-200 bg-surface-0 transition-[width] duration-200 lg:block dark:border-surface-800 dark:bg-surface-900"
      :class="collapsed ? 'w-14' : 'w-60'"
    >
      <AppSidebar v-model:collapsed="collapsed" />
    </aside>

    <Drawer v-model:visible="drawerOpen" data-test="sidebar-drawer" class="lg:hidden" :showCloseIcon="false">
      <AppSidebar :collapsed="false" @navigate="drawerOpen = false" />
    </Drawer>

    <div class="flex min-w-0 flex-1 flex-col">
      <AppTopbar @toggle-sidebar="drawerOpen = true" />

      <main data-test="main" class="min-h-0 flex-1" :class="fullHeight ? 'overflow-hidden' : 'overflow-y-auto'">
        <RouterView v-if="appStore.currentCompany" v-slot="{ Component }">
          <div class="p-6" :class="{ 'h-full': fullHeight }">
            <component :is="Component" />
          </div>
        </RouterView>

        <div
          v-else-if="appStore.companiesLoaded"
          data-test="no-company"
          class="flex h-full items-center justify-center p-6"
        >
          <EmptyState
            :icon="BuildingIcon"
            :title="t('layout.app.selectCompany')"
            :description="t('layout.app.selectCompanyDescription')"
          >
            <Button
              data-test="select-company"
              :label="t('layout.app.selectCompanyAction')"
              @click="appStore.requestCompanySwitcher()"
            />
          </EmptyState>
        </div>
      </main>
    </div>
  </div>
</template>
